import { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import { Typography, CircularProgress } from '@mui/material';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { getOneLink } from '../utils';
import { convertAndFilterMeasurements } from '../wasteConversion';
import { queryWasteMeasurementsForEvent } from 'src/schema';

// Register the required components and plugins
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface WasteMaterialChartProps {
    eventId: string;
    materialBreakdown: boolean;
    setGetChartBlob?: React.Dispatch<React.SetStateAction<((format: string) => Promise<Blob>) | null>>;
    itemIds?: string[];
    datasetIds?: string[];
}

// Use memo to prevent unnecessary re-renders
const WasteMaterialChart = memo(function WasteMaterialChartInner({
    eventId,
    setGetChartBlob,
    materialBreakdown,
    itemIds = [],
    datasetIds = [],
}: WasteMaterialChartProps) {
    const [chartInitialized, setChartInitialized] = useState(false);
    const chartInstanceRef = useRef<Chart | null>(null);

    /**
     * Crops a canvas to its content area
     * @param sourceCanvas The source canvas to crop
     * @param padding Optional padding around the content (default: 20)
     * @returns A new canvas with only the content area
     */
    const cropToContent = useCallback((sourceCanvas: HTMLCanvasElement, padding: number = 20): HTMLCanvasElement => {
        // Create a temporary canvas for cropping
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
            throw new Error('Could not get canvas context');
        }

        // Find the topmost and leftmost non-transparent pixel
        let top = sourceCanvas.height;
        let left = sourceCanvas.width;
        let bottom = 0;
        let right = 0;
        const imageData = sourceCanvas.getContext('2d')?.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
        if (imageData) {
            const data = imageData.data;
            for (let y = 0; y < sourceCanvas.height; y++) {
                for (let x = 0; x < sourceCanvas.width; x++) {
                    const index = y * sourceCanvas.width * 4 + x * 4;
                    if (data[index + 3] > 0) {
                        // Check alpha channel
                        if (x < left) left = x;
                        if (x > right) right = x;
                        if (y < top) top = y;
                        if (y > bottom) bottom = y;
                    }
                }
            }
        }

        // Adjust for padding
        left = Math.max(0, left - padding);
        top = Math.max(0, top - padding);
        right = Math.min(sourceCanvas.width - 1, right + padding);
        bottom = Math.min(sourceCanvas.height - 1, bottom + padding);

        // Calculate the width and height of the content area
        const contentWidth = right - left + 1;
        const contentHeight = bottom - top + 1;

        // Set the temp canvas dimensions to the content area size
        tempCanvas.width = contentWidth;
        tempCanvas.height = contentHeight;

        // Draw only the content area to the temp canvas
        tempCtx.drawImage(
            sourceCanvas,
            left,
            top,
            contentWidth,
            contentHeight, // Source rectangle
            0,
            0,
            contentWidth,
            contentHeight // Destination rectangle
        );

        return tempCanvas;
    }, []);

    // Function to get chart blob
    const getChartBlobFn = useCallback(
        async (format: string): Promise<Blob> => {
            if (!chartInstanceRef.current) {
                throw new Error('Chart is not available');
            }

            const canvas = chartInstanceRef.current.canvas;
            const croppedCanvas = cropToContent(canvas);

            return new Promise<Blob>((resolve, reject) => {
                croppedCanvas.toBlob(blob => {
                    if (blob) resolve(blob);
                    else reject(new Error('Failed to create image blob'));
                }, format);
            });
        },
        [cropToContent]
    );

    // Set up the getChartBlob function in the parent component
    useEffect(() => {
        if (setGetChartBlob) {
            setGetChartBlob(() => getChartBlobFn);
        }

        // Cleanup function
        return () => {
            if (setGetChartBlob) {
                setGetChartBlob(null);
            }
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [setGetChartBlob, getChartBlobFn]);

    // Fetch data for the chart using db.useQuery
    const { data, isLoading, error } = queryWasteMeasurementsForEvent(eventId);

    // Derive chart data from fetched measurements
    const chartData = useMemo(() => {
        if (!data || error) return { labels: [], data: [] };

        // Convert and filter measurements
        const convertedMeasurements = convertAndFilterMeasurements(data.wasteMeasurements || [], {
            materialBreakdown,
            itemIds,
            datasetIds,
            targetUnit: 'lb',
        });

        if (materialBreakdown) {
            // Group by material name and sum quantities
            const materialTotals: Record<string, number> = {};

            for (const measurement of convertedMeasurements) {
                const material = getOneLink(measurement?.material);
                if (material?.name) {
                    if (!materialTotals[material.name]) {
                        materialTotals[material.name] = 0;
                    }
                    materialTotals[material.name] += measurement.quantity;
                }
            }

            return {
                labels: Object.keys(materialTotals),
                data: Object.values(materialTotals),
            };
        } else {
            // Group by category name and sum quantities
            const categoryTotals: Record<string, number> = {};

            for (const measurement of convertedMeasurements) {
                const material = getOneLink(measurement?.material);
                const category = getOneLink(material?.category);

                if (category?.name) {
                    if (!categoryTotals[category.name]) {
                        categoryTotals[category.name] = 0;
                    }
                    categoryTotals[category.name] += measurement.quantity;
                }
            }

            return {
                labels: Object.keys(categoryTotals),
                data: Object.values(categoryTotals),
            };
        }
    }, [data, error, materialBreakdown, itemIds, datasetIds]);

    // Generate random colors for chart segments
    const generateColors = useCallback((count: number) => {
        const colors = [];
        for (let i = 0; i < count; i++) {
            const hue = (i * 137) % 360; // Use golden angle to spread colors
            colors.push(`hsl(${hue}, 70%, 60%)`);
        }
        return colors;
    }, []);

    // Update chart options based on materialBreakdown
    useEffect(() => {
        if (!chartInitialized || !chartInstanceRef.current || chartData.labels.length === 0) return;

        // Update chart data without recreating the chart
        chartInstanceRef.current.data.labels = chartData.labels;
        chartInstanceRef.current.data.datasets[0].data = chartData.data;
        chartInstanceRef.current.data.datasets[0].backgroundColor = generateColors(chartData.labels.length);

        // Update chart title based on materialBreakdown
        if (
            chartInstanceRef.current.options &&
            chartInstanceRef.current.options.plugins &&
            chartInstanceRef.current.options.plugins.title
        ) {
            chartInstanceRef.current.options.plugins.title.text = materialBreakdown ? 'Waste by Material (lbs)' : 'Waste by Category (lbs)';
        }

        // Update the chart
        chartInstanceRef.current.update();
    }, [chartData, generateColors, chartInitialized, materialBreakdown]);

    // Callback ref for the canvas element
    const canvasRef = useCallback(
        (canvas: HTMLCanvasElement | null) => {
            if (!canvas) return;

            // Destroy previous chart instance if it exists
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            // Create new chart
            const ctx = canvas.getContext('2d');
            if (ctx === null) {
                throw new Error('Could not get canvas context');
            }

            chartInstanceRef.current = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: chartData.labels,
                    datasets: [
                        {
                            data: chartData.data,
                            backgroundColor: generateColors(chartData.labels.length),
                            borderWidth: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'right',
                        },
                        title: {
                            display: true,
                            text: materialBreakdown ? 'Waste by Material (lbs)' : 'Waste by Category (lbs)',
                        },
                        tooltip: {
                            callbacks: {
                                label: context => {
                                    const total = context.dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                                    const value = context.raw as number;
                                    const percentage = ((value / total) * 100).toFixed(1);
                                    return `${context.label}: ${value.toFixed(1)} lbs (${percentage}%)`;
                                },
                            },
                        },
                        datalabels: {
                            formatter: (value, context) => {
                                const total = (context.dataset.data as number[]).reduce((sum: number, val: number) => sum + val, 0);
                                const percentage = (((value as number) / total) * 100).toFixed(1);
                                return `${percentage}%`;
                            },
                            color: '#000',
                            font: {
                                weight: 'bold',
                            },
                            display: context => (context.dataset.data as number[])[context.dataIndex] > 0,
                        },
                    },
                },
            });

            // Mark the chart as initialized
            setChartInitialized(true);
        },
        [chartData, generateColors, materialBreakdown]
    );

    if (isLoading) {
        return <CircularProgress sx={{ my: 'auto' }} />;
    }

    if (chartData.labels.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ my: 'auto' }}>
                No waste measurement data available in lbs units
            </Typography>
        );
    }

    return <canvas ref={canvasRef} style={{ maxWidth: '100%', height: '100%' }} />;
});

export default WasteMaterialChart;
